import * as React from 'react';
import type { StepAction, StepControlsView } from './controls.ts';

// The steps bar drawn from a `StepControlsView`: three zones in a row. The
// centre holds the steps, first ‹ › last, or, while an owner holds the
// controls (a puzzle), look back ‹ and back to the puzzle ↶ only. The right
// holds the contextual exit, a secondary button with an icon and a label
// (icon only on a narrow screen): "Back to the game" on a line of the
// reader's own, "Back to the puzzle" while looking back in one, nothing
// otherwise. The left is empty and as wide as the right, so the centre is
// centred and never shifts when the exit comes or goes. The status line
// beneath is for assistive tech only; `children` go under it (the material
// strip). Presentational: the data as props, the clicks reported by action.

export interface StepControlsProps {
  view: StepControlsView;
  /** A step or the exit was clicked: the review method to call. */
  onAction: (action: StepAction) => void;
  /** Whatever other plugins put at the foot, the material strip first. */
  children?: React.ReactNode;
}

const Icon = {
  start: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M56 40v176M200 48v160L80 128z" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M160 48 80 128l80 80" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  forward: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="m96 48 80 80-80 80" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  end: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M200 40v176M56 48v160l120-80z" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Phosphor arrow-u-up-left: back to the puzzle's position after looking back.
  puzzle: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="m80 56-48 48 48 48M32 104h108a52 52 0 0 1 0 104h-20" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Phosphor arrow-bend-up-left: back up the line to where it left the game.
  return: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M80 56 32 104l48 48M32 104h104a64 64 0 0 1 64 64v40" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

/** The key that does the same as the exit, for its title. */
const EXIT_KEY: Record<StepAction, string> = { toStart: '↑', stepBackward: '←', stepForward: '→', toEnd: '↓', returnToGame: 'Esc' };

export const StepControls: React.FC<StepControlsProps> = ({ view, onAction, children }) => {
  const { ready, held, deviated, labels, zones } = view;
  const canBack = ready && view.canBack;
  const canForward = ready && view.canForward;
  const exit = zones.right;

  const step = (icon: React.ReactNode, label: string, key: string, enabled: boolean, action: StepAction, extra = '') => (
    <button
      type="button"
      className={`btn btn-ghost btn-icon step${extra}`}
      title={`${label} (${key})`}
      aria-label={label}
      disabled={!enabled}
      onClick={() => onAction(action)}>
      {icon}
    </button>
  );

  return (
    <section
      className={`panel review-step-controls${deviated ? ' review-step-controls-line' : ''}${held ? ' review-step-controls-held' : ''}`}
      aria-label={held ? 'Look back through the puzzle' : 'Move through the game'}>
      <div className="steps">
        <div className="steps-left" aria-hidden="true" />
        <div className="steps-centre">
          {zones.centre === 'held' ? (
            <>
              {step(Icon.back, labels.back, '←', canBack, 'stepBackward')}
              {step(Icon.puzzle, labels.end, '↓', canForward, 'toEnd', ' step-puzzle')}
            </>
          ) : (
            <>
              {step(Icon.start, labels.start, '↑', canBack, 'toStart')}
              {step(Icon.back, labels.back, '←', canBack, 'stepBackward')}
              {step(Icon.forward, labels.forward, '→', canForward, 'stepForward')}
              {step(Icon.end, labels.end, '↓', canForward, 'toEnd')}
            </>
          )}
        </div>
        <div className="steps-right">
          {exit && (
            <button
              type="button"
              className={`btn btn-secondary step-exit step-exit-${exit.kind}`}
              title={`${exit.label} (${EXIT_KEY[exit.action]})`}
              aria-label={exit.label}
              onClick={() => onAction(exit.action)}>
              {exit.kind === 'game' ? Icon.return : Icon.puzzle}
              <span className="step-exit-label">{exit.label}</span>
            </button>
          )}
        </div>
      </div>
      {/* The status is read by assistive tech only; the sheet and board say where the reader is. */}
      <p className="step-status visually-hidden" aria-live="polite">
        {view.status}
      </p>
      {children}
    </section>
  );
};
